import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { LifeBuoy, Mail, MessageCircle, Sparkles, ArrowLeft, Copy, ExternalLink, Clock, MapPin, Phone } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { COMPANY_INFO } from '../components/Footer';

export default function ContactUs() {
  const { language } = useLanguage();
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();

  const SUPPORT_EMAIL = COMPANY_INFO.email;
  const WHATSAPP_NUMBER_RAW = COMPANY_INFO.phone.replace(/[^0-9]/g, ''); // e.g. 971501511917
  const WHATSAPP_DISPLAY = COMPANY_INFO.phoneDisplay;

  const emailSubject = language === 'ar' ? 'استفسار عن CloudGM Business' : 'CloudGM Business Inquiry';
  const emailBody = language === 'ar'
    ? `مرحباً فريق CloudGM،%0D%0A%0D%0Aلديّ استفسار حول:%0D%0A%0D%0A`
    : `Hello CloudGM Team,%0D%0A%0D%0AI have an inquiry about:%0D%0A%0D%0A`;
  const mailtoLink = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(emailSubject)}&body=${emailBody}`;

  const whatsappMessage = language === 'ar'
    ? 'مرحباً، أحتاج مساعدة بخصوص CloudGM Business'
    : 'Hello, I need help regarding CloudGM Business';
  const whatsappLink = `https://wa.me/${WHATSAPP_NUMBER_RAW}?text=${encodeURIComponent(whatsappMessage)}`;

  const handleCopyEmail = () => {
    navigator.clipboard.writeText(SUPPORT_EMAIL);
    toast.success(language === 'ar' ? 'تم نسخ البريد الإلكتروني' : 'Email copied');
  };

  const handleCopyPhone = () => {
    navigator.clipboard.writeText(COMPANY_INFO.phone);
    toast.success(language === 'ar' ? 'تم نسخ رقم الهاتف' : 'Phone number copied');
  };

  const handleAiChat = () => {
    // /support requires login; ProtectedRoute will redirect to /login if not authenticated
    navigate('/support');
  };

  const channels = [
    {
      key: 'email',
      icon: Mail,
      iconGradient: 'from-[#38BDF8] to-[#6366F1]',
      title: language === 'ar' ? 'البريد الإلكتروني' : 'Email',
      subtitle: language === 'ar' ? 'رد خلال 24 ساعة عادة' : 'Usually replies within 24 hours',
      value: SUPPORT_EMAIL,
      valueDir: 'ltr',
      primaryLabel: language === 'ar' ? 'أرسل بريد' : 'Send Email',
      onPrimary: () => window.location.href = mailtoLink,
      secondaryLabel: language === 'ar' ? 'نسخ' : 'Copy',
      onSecondary: handleCopyEmail,
      isExternal: true,
      testId: 'support-email',
    },
    {
      key: 'whatsapp',
      icon: MessageCircle,
      iconGradient: 'from-[#22C55E] to-[#16A34A]',
      title: language === 'ar' ? 'واتساب' : 'WhatsApp',
      subtitle: language === 'ar' ? 'تواصل فوري مع فريقنا' : 'Instant chat with our team',
      value: WHATSAPP_DISPLAY,
      valueDir: 'ltr',
      primaryLabel: language === 'ar' ? 'محادثة واتساب' : 'Chat on WhatsApp',
      onPrimary: () => window.open(whatsappLink, '_blank', 'noopener,noreferrer'),
      secondaryLabel: language === 'ar' ? 'نسخ' : 'Copy',
      onSecondary: handleCopyPhone,
      isExternal: true,
      testId: 'support-whatsapp',
    },
    {
      key: 'ai',
      icon: Sparkles,
      iconGradient: 'from-[#F97316] to-[#EC4899]',
      title: language === 'ar' ? 'الذكاء الاصطناعي' : 'AI Assistant',
      subtitle: language === 'ar' ? 'ردود فورية على مدار الساعة' : 'Instant answers, 24/7',
      value: language === 'ar' ? 'CloudGM AI' : 'CloudGM AI',
      valueDir: 'ltr',
      primaryLabel: language === 'ar' ? 'ابدأ محادثة' : 'Start Chat',
      onPrimary: handleAiChat,
      secondaryLabel: null,
      onSecondary: null,
      isExternal: false,
      testId: 'support-ai-chat',
    },
  ];

  return (
    <div className={`min-h-screen cgm-page`}>
      {/* Compact Hero Section */}
      <section className="relative overflow-hidden py-8 sm:py-10">
        <div className="absolute inset-0 pointer-events-none">
          <div className={`absolute inset-0 ${isDarkMode ? 'bg-gradient-to-r from-[#0F172A] to-[#1E293B]' : 'bg-gradient-to-r from-[#38BDF8] to-[#F97316]'}`} />
          <div className="absolute top-0 end-0 w-64 h-64 rounded-full bg-white/10 blur-3xl" />
          <div className="absolute bottom-0 start-0 w-48 h-48 rounded-full bg-white/10 blur-3xl" />
        </div>

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-4 transition-colors"
            data-testid="contact-us-back-btn"
          >
            <ArrowLeft className="h-4 w-4 rtl:rotate-180" />
            <span className="text-sm">{language === 'ar' ? 'الرئيسية' : 'Home'}</span>
          </Link>

          <div className="text-center">
            <div className="w-16 h-16 mx-auto glass-card rounded-2xl flex items-center justify-center mb-4">
              <LifeBuoy className="h-8 w-8 text-white" />
            </div>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-3" data-testid="contact-us-title">
              {language === 'ar' ? 'مركز الدعم' : 'Support Center'}
            </h1>
            <p className="text-base text-white/90 max-w-2xl mx-auto">
              {language === 'ar'
                ? 'اختر الطريقة الأنسب لك للتواصل مع فريق CloudGM Business'
                : 'Choose the way that works best for you to reach the CloudGM Business team'}
            </p>
          </div>
        </div>
      </section>

      {/* Channels */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-5">
          {channels.map((ch) => (
            <div
              key={ch.key}
              className={`group relative overflow-hidden rounded-2xl border p-5 sm:p-6 flex flex-col shadow-lg transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl ${
                isDarkMode
                  ? 'bg-[#131B2C] border-white/10'
                  : 'bg-white border-black/5'
              }`}
              data-testid={`${ch.testId}-card`}
            >
              {/* Icon */}
              <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${ch.iconGradient} flex items-center justify-center mb-4 shadow-lg`}>
                <ch.icon className="h-7 w-7 text-white" />
                {ch.isExternal && (
                  <ExternalLink className="absolute top-4 end-4 h-3.5 w-3.5 text-gray-400 opacity-60" />
                )}
              </div>

              {/* Title */}
              <h3 className={`text-lg font-bold mb-1 ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                {ch.title}
              </h3>
              <p className={`text-sm mb-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                {ch.subtitle}
              </p>

              {/* Value */}
              <div className={`text-sm mb-5 font-medium truncate ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`} dir={ch.valueDir}>
                {ch.value}
              </div>

              {/* Actions */}
              <div className="mt-auto flex items-center gap-2">
                <button
                  onClick={ch.onPrimary}
                  className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-white gradient-primary hover:opacity-90 transition-opacity"
                  data-testid={`${ch.testId}-primary-btn`}
                >
                  <ch.icon className="h-4 w-4" />
                  {ch.primaryLabel}
                </button>
                {ch.secondaryLabel && (
                  <button
                    onClick={ch.onSecondary}
                    className={`inline-flex items-center justify-center gap-1 px-3 py-2.5 rounded-xl text-sm font-medium transition-all border ${
                      isDarkMode
                        ? 'bg-white/5 border-white/10 text-white hover:bg-white/10'
                        : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100'
                    }`}
                    aria-label={ch.secondaryLabel}
                    data-testid={`${ch.testId}-copy-btn`}
                  >
                    <Copy className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Working Hours & Address */}
        <div className={`mt-6 rounded-2xl p-5 sm:p-6 border grid grid-cols-1 sm:grid-cols-2 gap-4 ${
          isDarkMode
            ? 'bg-[#131B2C] border-white/10'
            : 'bg-white border-black/5'
        }`}>
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl gradient-primary flex items-center justify-center flex-shrink-0">
              <Clock className="h-5 w-5 text-white" />
            </div>
            <div>
              <h4 className={`text-sm font-bold mb-1 ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                {language === 'ar' ? 'ساعات العمل' : 'Working Hours'}
              </h4>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                {language === 'ar'
                  ? 'الأحد – الخميس: 9:00 صباحاً – 6:00 مساءً'
                  : 'Sun – Thu: 9:00 AM – 6:00 PM'}
                <br />
                {language === 'ar'
                  ? 'الذكاء الاصطناعي: 24/7'
                  : 'AI Assistant: 24/7'}
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl gradient-primary flex items-center justify-center flex-shrink-0">
              <MapPin className="h-5 w-5 text-white" />
            </div>
            <div>
              <h4 className={`text-sm font-bold mb-1 ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                {language === 'ar' ? 'العنوان' : 'Address'}
              </h4>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                {language === 'ar' ? COMPANY_INFO.address.ar : COMPANY_INFO.address.en}
              </p>
            </div>
          </div>
        </div>

        {/* Quick contact strip */}
        <div className={`mt-4 rounded-2xl p-4 sm:p-5 border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 ${
          isDarkMode
            ? 'bg-gradient-to-r from-[#38BDF8]/10 to-[#F97316]/10 border-white/10'
            : 'bg-gradient-to-r from-[#38BDF8]/5 to-[#F97316]/5 border-[#38BDF8]/20'
        }`}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl gradient-primary flex items-center justify-center">
              <Phone className="h-5 w-5 text-white" />
            </div>
            <div>
              <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                {language === 'ar' ? 'اتصل بنا مباشرة' : 'Call us directly'}
              </p>
              <a href={`tel:${COMPANY_INFO.phone}`} className={`text-base font-bold ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`} dir="ltr">
                {WHATSAPP_DISPLAY}
              </a>
            </div>
          </div>
          <p className={`text-xs sm:text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            {language === 'ar'
              ? 'نحن هنا لمساعدتك في كل خطوة'
              : 'We are here to help you every step of the way'}
          </p>
        </div>
      </section>
    </div>
  );
}
